import { useState } from 'react';
import {
  ALL_SPELLS,
  ATTACK_TYPES,
  DAMAGE_TYPES,
  type CharacterDef,
  type GameDefinition,
} from '@archon3d/core';

export function CharacterEditor({
  characters,
  portraits,
  onChange,
  resetTemplate,
}: {
  characters: CharacterDef[];
  portraits: Record<string, string>;
  onChange: (characters: CharacterDef[]) => void;
  resetTemplate: GameDefinition;
}) {
  const [id, setId] = useState(characters[0]!.id);
  const [numberError, setNumberError] = useState<string | null>(null);
  const c = characters.find((c) => c.id === id) ?? characters[0]!;
  const edit = (patch: Partial<CharacterDef>) =>
    onChange(characters.map((a) => (a.id === c.id ? { ...a, ...patch } : a)));
  const number = (
    label: string,
    value: number,
    min: number,
    max: number,
    change: (n: number) => void,
    step = 1,
    integer = false,
  ) => (
    <NumberField
      key={`${c.id}:${label}`}
      label={label}
      value={value}
      min={min}
      max={max}
      step={step}
      integer={integer}
      onValue={change}
      onError={setNumberError}
    />
  );
  return (
    <section>
      <h3>Character abilities · this game only</h3>
      <p className="hint">
        Starts with an independent copy of the original roster. Changes affect only this game,
        including summoned creatures. Strength is the balancing/AI value; attack damage controls
        damage in combat. Combat values are the Expert baseline; other difficulties automatically
        apply their percentage multipliers to these values, including this character’s adjustments.
        Card ratings and matchup estimates follow these same Expert properties automatically. HP and
        Damage are shown rounded; new values must be whole numbers. Existing values keep their
        precision until you edit them.
      </p>
      {numberError && <p role="alert">{numberError}</p>}
      <div className="editor-body">
        <aside className="editor-side-panel character-list">
          {characters.map((a) => (
            <button
              key={a.id}
              className={c.id === a.id ? 'active' : ''}
              onClick={() => {
                setId(a.id);
                setNumberError(null);
              }}
            >
              {portraits[a.id] && <img src={portraits[a.id]} alt="" />}
              {a.name}
              {a.summoned ? ' (summoned)' : ''}
            </button>
          ))}
        </aside>
        <div className="editor-stage character-fields">
          <h3>{c.name}</h3>
          <button
            onClick={() => {
              const original = resetTemplate.characters.find((a) => a.id === c.id);
              // The original whole, not laid over the edited one: fields it never had (spells, a
              // spellcaster's flag) go too (editor:bugs#4).
              if (original && confirm(`Reset ${c.name} from ${resetTemplate.name}?`))
                onChange(characters.map((a) => (a.id === c.id ? structuredClone(original) : a)));
            }}
          >
            Reset character from {resetTemplate.name}
          </button>
          <fieldset>
            <legend>Strength, health & movement</legend>
            {number('Strength points', c.force ?? 50, 1, 1000, (force) => edit({ force }), 1, true)}
            {number(
              'Health points',
              c.hitPoints,
              1,
              1000,
              (hitPoints) => edit({ hitPoints }),
              1,
              true,
            )}
            {number('Tier', c.tier, 1, 5, (tier) => edit({ tier }), 1, true)}
            <label className="editor-field">
              Movement
              <select
                value={c.movement.type}
                onChange={(e) =>
                  edit({
                    movement: {
                      ...c.movement,
                      type: e.target.value as CharacterDef['movement']['type'],
                    },
                  })
                }
              >
                {['ground', 'fly', 'teleport'].map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </label>
            {number(
              'Movement range',
              c.movement.range,
              1,
              12,
              (range) => edit({ movement: { ...c.movement, range } }),
              1,
              true,
            )}
          </fieldset>
          <fieldset>
            <legend>Expert combat ability</legend>
            <label className="editor-field">
              Attack
              <select
                aria-label="Attack type"
                value={c.attack.type}
                onChange={(e) =>
                  edit({
                    attack: {
                      ...c.attack,
                      type: e.target.value as CharacterDef['attack']['type'],
                      ...(e.target.value !== 'burn'
                        ? { reach: undefined, duration: undefined }
                        : {}),
                    },
                  })
                }
              >
                {ATTACK_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t === 'projectile'
                      ? 'Shoot / projectile'
                      : t === 'burn'
                        ? 'Circle burn / ring of fire'
                        : t}
                  </option>
                ))}
              </select>
            </label>
            {number(
              'Attack damage',
              c.attack.damage,
              1,
              1000,
              (damage) => edit({ attack: { ...c.attack, damage } }),
              1,
              true,
            )}
            {number(
              'Attack interval (seconds)',
              c.attack.interval ??
                (c.attack.type === 'melee' ? 0.6 : c.attack.type === 'aura' ? 0.5 : 1),
              0.1,
              60,
              (interval) => edit({ attack: { ...c.attack, interval } }),
              0.1,
            )}
            <label className="editor-field">
              Damage type
              <select
                value={c.attack.damageType ?? 'physical'}
                onChange={(e) =>
                  edit({
                    attack: {
                      ...c.attack,
                      damageType: e.target.value as CharacterDef['attack']['damageType'],
                    },
                  })
                }
              >
                {DAMAGE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>
            {number('Effect chance (%)', 100 * (c.attack.effectChance ?? 1), 0, 100, (n) =>
              edit({ attack: { ...c.attack, effectChance: n / 100 } }),
            )}
            {number(
              'Arena speed multiplier',
              c.combat?.speed ?? 1,
              0.4,
              2,
              (speed) => edit({ combat: { ...c.combat, speed } }),
              0.01,
            )}
            {number('Protection (%)', 100 * (c.combat?.protection ?? 0), 0, 75, (n) =>
              edit({ combat: { ...c.combat, protection: n / 100 } }),
            )}
            {c.attack.type === 'burn' && (
              <>
                {number(
                  'Burn reach (body heights)',
                  c.attack.reach ?? 2,
                  0.5,
                  5,
                  (reach) => edit({ attack: { ...c.attack, reach } }),
                  0.1,
                )}
                {number(
                  'Burn duration (seconds)',
                  c.attack.duration ?? 3,
                  1,
                  10,
                  (duration) => edit({ attack: { ...c.attack, duration } }),
                  0.1,
                )}
              </>
            )}
          </fieldset>
          <fieldset>
            <legend>Expert resistances</legend>
            {DAMAGE_TYPES.map((type) => (
              <div key={type}>
                {number(
                  `${type} resistance (%)`,
                  100 * (c.combat?.resistances?.[type] ?? 0),
                  0,
                  95,
                  (n) =>
                    edit({
                      combat: {
                        ...c.combat,
                        resistances: { ...c.combat?.resistances, [type]: n / 100 },
                      },
                    }),
                )}
              </div>
            ))}
          </fieldset>
          <fieldset>
            <legend>Magic</legend>
            {number(
              'Spell points',
              c.spellPoints ?? 0,
              0,
              1000,
              (spellPoints) => edit({ spellPoints }),
              1,
              true,
            )}
            <label>
              <input
                type="checkbox"
                checked={c.spellcaster ?? false}
                onChange={(e) => edit({ spellcaster: e.target.checked })}
              />{' '}
              Spellcaster / leader
            </label>
            <div className="spell-palette">
              {ALL_SPELLS.map((spell) => (
                <label key={spell}>
                  <input
                    type="checkbox"
                    checked={c.spells?.includes(spell) ?? false}
                    onChange={(e) =>
                      edit({
                        spells: e.target.checked
                          ? [...(c.spells ?? []), spell]
                          : (c.spells ?? []).filter((s) => s !== spell),
                      })
                    }
                  />
                  {spell.replaceAll('-', ' ')}
                </label>
              ))}
            </div>
          </fieldset>
        </div>
      </div>
    </section>
  );
}

/**
 * A number field that tolerates half-typed values (editor:bugs#5): what is typed stays in the
 * field while it is not yet a value the character may take ("0" on the way to 0.8), and the
 * character changes as soon as it is one. Leaving the field shows the character's value again.
 * Decimals are shown to the hundredth: 39.3, not the 39.300000000000004 that 100 × 0.393 makes
 * (editor:quality#8); the stored value keeps its precision until the field is edited.
 */
function NumberField({
  label,
  value,
  min,
  max,
  step,
  integer,
  onValue,
  onError,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  integer: boolean;
  onValue: (n: number) => void;
  onError: (message: string | null) => void;
}) {
  const shownValue = integer ? Math.round(value) : Math.round(value * 100) / 100;
  const [text, setText] = useState(String(shownValue));
  const [shown, setShown] = useState(shownValue);
  if (shown !== shownValue) {
    setShown(shownValue);
    setText(String(shownValue));
  }
  return (
    <label className="editor-field">
      <span>{label}</span>
      <input
        type="number"
        aria-label={label}
        min={min}
        max={max}
        step={step}
        inputMode={integer ? 'numeric' : undefined}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const n = Number(e.target.value);
          if (e.target.value.trim() === '' || !Number.isFinite(n)) return;
          // A decimal in a field of whole numbers is refused at once and said why.
          if (integer && !Number.isInteger(n)) {
            setText(String(shownValue));
            onError(`${label} must be a whole number.`);
            return;
          }
          if (n >= min && n <= max) {
            onError(null);
            onValue(n);
          }
        }}
        onBlur={() => setText(String(shownValue))}
      />
    </label>
  );
}
